import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface ParallaxImageProps {
    src: string
    alt: string
    className?: string
    imageClassName?: string
    distance?: number
    priority?: boolean
}

// An image that drifts inside its frame while the frame crosses the viewport.
export const ParallaxImage: FC<ParallaxImageProps> = ({
    src,
    alt,
    className,
    imageClassName,
    distance = 10,
    priority = false
}): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduced = usePrefersReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
    const y = useTransform(scrollYProgress, [0, 1], reduced ? ['0%', '0%'] : [`-${distance}%`, `${distance}%`])

    return (
        <div ref={ref} className={cn('relative overflow-hidden', className)}>
            <motion.img
                src={src}
                alt={alt}
                loading={priority ? 'eager' : 'lazy'}
                decoding='async'
                className={cn('absolute inset-x-0 -top-[15%] h-[130%] w-full object-cover', imageClassName)}
                style={{ y }}
            />
        </div>
    )
}
